import { Input } from "@components/common/input";
import { PlusCircle } from "lucide-react";
import {
  SERVER_URL,
  USER_ROLE_DICTIONARY_ENDPOINT,
} from "@pma/utils/api-routes";
import { useAuth } from "@stores/auth-store";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import useTranslator from "@hooks/use-translator";
import { DictType } from "@pma/types";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormField, FormItem } from "@components/common/form";
import { toast } from "@hooks/use-toast";
import { Button } from "@components/common/button";

const formSchema = z.object({
  name: z.string({
    message: "Enter role name",
  }),
});

const defaultRolePermissions = {
  full_access: false,
  edit_any_project: false,
  proh_edit_project_as_member: false,
  view_any_project: false,
  create_new_project: false,
  delete_projects: false,
  edit_any_task: false,
  proh_edit_task_as_member: false,
  edit_organization_setting: false,
  edit_user_settings: false,
  manage_any_folders: false,
  proh_manage_folders_as_member: false,
  files_delete: false,
  file_restore: false,
  manage_any_teams: false,
};

const CreateRole = ({ refetch }: any) => {
  const { translate, locale } = useTranslator();

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const { authConfig } = useAuth();

  const orgId = authConfig?.last_organization_id;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  });

  const addRole = async (role: string) => {
    const response = await axios.post(
      SERVER_URL + USER_ROLE_DICTIONARY_ENDPOINT + "/add",
      {
        name: role,
        organization_id: orgId,

        ...defaultRolePermissions,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };
  const addRoleMutation = useMutation({
    mutationKey: ["add-role"],
    mutationFn: addRole,
    onSuccess: (data) => {
      form.reset();
      refetch && refetch();
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data.message ||
                `${translate(`Error occurred while updating`)} ${translate(`department`)}`,
        variant: "error",
      });
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    addRoleMutation.mutate(data?.name);
  };

  return (
    <Popover>
      <PopoverTrigger>
        <PlusCircle />
      </PopoverTrigger>
      <PopoverContent className="w-fit">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <Input
                    placeholder={translate("Add Role")}
                    className="rounded-full"
                    value={field.value || ""}
                    onChange={field.onChange}
                  />
                </FormItem>
              )}
            />

            <div className="flex w-full justify-center">
              <Button size={"sm"} className="w-2/3">
                {translate("Save")}
              </Button>
            </div>
          </form>
        </Form>
      </PopoverContent>
    </Popover>
  );
};

export default CreateRole;
